// src/hooks/useCommune.ts

import { useState, useEffect } from 'react';
import communeService from '../services/commune.service';
import type { Commune } from '../types/api.types';

/**
 * Hook to fetch commune data by ID
 */
export const useCommune = (communeId: string | null) => {
  const [commune, setCommune] = useState<Commune | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!communeId) {
      setCommune(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchCommune = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await communeService.getCommuneById(communeId);
        setCommune(response.data);
      } catch (err: any) {
        console.error('Failed to fetch commune:', err);
        setError(err.message || 'Failed to fetch commune');
        setCommune(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCommune();
  }, [communeId]);

  return {
    commune,
    loading,
    error,
    communeName: commune?.communeName || null,
  };
};

/**
 * Hook to get context info with commune name instead of ID
 */
export const useContextInfo = (
  isSuperAdmin: () => boolean,
  isCommunalAdmin: () => boolean,
  isGroupAgent: () => boolean,
  getAssignedCommuneId: () => string | null,
  getAssignedGroupId: () => string | null
) => {
  const communeId = isCommunalAdmin() ? getAssignedCommuneId() : null;
  const { commune, loading: communeLoading } = useCommune(communeId);

  const getContextInfo = () => {
    if (isSuperAdmin()) return null;
    
    if (isCommunalAdmin()) {
      const communeId = getAssignedCommuneId();
      return { 
        type: 'commune' as const, 
        id: communeId, 
        label: 'Commune assignée',
        name: commune?.communeName || communeId || 'Commune inconnue',
        loading: communeLoading
      };
    }
    
    if (isGroupAgent()) {
      const groupId = getAssignedGroupId();
      return { 
        type: 'group' as const, 
        id: groupId, 
        label: 'Groupe assigné',
        name: groupId || 'Groupe inconnu', // Could extend this to fetch group name too
        loading: false
      };
    }
    
    return null;
  };

  return { getContextInfo };
};

export default useCommune;